import React, { useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { gql } from "@apollo/client";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import MsgClock1Icon from "@src/lib/icons/18px/msg-clock-1";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import PaginatedList from "@src/features/library/PaginatedList";
import { cn } from "@src/lib/utils/cn";
import { getFormattedDistanceToNow } from "@src/features/libraryItem/course/utils/utils";
import { OpusAgentChatTitleSource } from "@src/types.generated";
import {
  OpusAgentChatListItemFragment,
  useOpusAgentChatsQuery,
  useRenameOpusAgentChatMutation,
} from "./OpusAgentChatHistoryButton.generated";

export const UNTITLED_CHAT_LABEL = "Untitled chat";
// Mirrors scadmin's TITLE_MAX_LENGTH (opus_agent/controllers/titles.py),
// which rename_opus_agent_chat enforces with a bare ValueError — a codeless
// GraphQL error to the client — so the input pre-validates instead.
export const CHAT_TITLE_MAX_LENGTH = 255;
// Show the remaining-characters counter only this close to the cap.
const TITLE_COUNTER_THRESHOLD = 20;

// cache-and-network so reopening the popover refreshes titles/lastActivity
// (the AI titling pass lands after a turn finishes); cache-first afterwards
// keeps fetchMore pagination from refetching page one.
const useChatsListQuery: typeof useOpusAgentChatsQuery = (options) =>
  useOpusAgentChatsQuery({
    ...options,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

interface ChatRowProps {
  chat: OpusAgentChatListItemFragment;
  isActive: boolean;
  onSelect: () => void;
}

// One chat in the history list: click selects, the hover pencil renames
// inline. Renames land optimistically (MANUAL pins the title against the
// background AI titling pass); the mutation payload carries the
// authoritative chat and settles the cache by normalization.
const ChatRow: React.FC<ChatRowProps> = ({ chat, isActive, onSelect }) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  // Closing the editor (Enter, Escape, or the state flip itself) unmounts a
  // focused input, and browsers fire its blur while it leaves the DOM. This
  // flag settles the rename FIRST, so that trailing blur can neither commit
  // an escaped draft nor double-submit an entered one.
  const renameSettledRef = useRef(false);
  const [renameOpusAgentChat] = useRenameOpusAgentChatMutation();

  const startRenaming = (event: React.MouseEvent) => {
    event.stopPropagation();
    renameSettledRef.current = false;
    setDraft(chat.title ?? "");
    setIsRenaming(true);
  };

  const cancelRename = () => {
    renameSettledRef.current = true;
    setIsRenaming(false);
  };

  // Enter with an empty/whitespace draft is ignored (the editor stays open);
  // blur with one cancels. Either way nothing empty reaches the server.
  const submitRename = ({ viaBlur }: { viaBlur: boolean }) => {
    if (renameSettledRef.current) return;
    const title = draft.trim().slice(0, CHAT_TITLE_MAX_LENGTH);
    if (!title && !viaBlur) return;
    renameSettledRef.current = true;
    setIsRenaming(false);
    if (!title || title === chat.title) return;
    renameOpusAgentChat({
      variables: { id: chat.id, title },
      optimisticResponse: {
        renameOpusAgentChat: {
          __typename: "RenameOpusAgentChatResult",
          success: true,
          error: null,
          chat: {
            ...chat,
            title,
            titleSource: OpusAgentChatTitleSource.Manual,
          },
        },
      },
    }).catch(() => undefined);
  };

  if (isRenaming) {
    const remaining = CHAT_TITLE_MAX_LENGTH - draft.length;
    return (
      <div className="px-1.5 py-1">
        <input
          value={draft}
          maxLength={CHAT_TITLE_MAX_LENGTH}
          onChange={(event) =>
            setDraft(event.target.value.slice(0, CHAT_TITLE_MAX_LENGTH))
          }
          onBlur={() => submitRename({ viaBlur: true })}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submitRename({ viaBlur: false });
            }
            if (event.key === "Escape") cancelRename();
          }}
          autoFocus
          data-testid="opus-agent-chat-rename-input"
          className="w-full rounded-md border border-input bg-white px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-ring"
        />
        {remaining <= TITLE_COUNTER_THRESHOLD && (
          <Text
            variant="fine-print-title"
            className="mt-0.5 block text-right text-muted-foreground"
            data-testid="opus-agent-chat-rename-counter"
          >
            {draft.length}/{CHAT_TITLE_MAX_LENGTH}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex cursor-pointer items-center justify-between gap-1 rounded-md bg-white px-1.5 py-1 transition-colors duration-200 hover:bg-gray-1",
        isActive && "bg-gray-1",
      )}
      onClick={onSelect}
      data-testid={`opus-agent-chat-row-${chat.id}`}
    >
      <Text
        variant="fine-print-description"
        ellipsis
        style={{ flex: 1, alignSelf: "stretch" }}
        className="text-left"
      >
        {chat.title || UNTITLED_CHAT_LABEL}
      </Text>
      <Button
        variant="ghost"
        size="icon-xs"
        className="shrink-0 opacity-0 group-hover:opacity-100"
        onClick={startRenaming}
        title="Rename chat"
        data-testid={`opus-agent-chat-rename-${chat.id}`}
      >
        <PenOutlineIcon className="size-3 text-muted-foreground" />
      </Button>
      <Text
        variant="fine-print-description"
        className="text-right text-muted-foreground"
      >
        {getFormattedDistanceToNow(
          new Date(chat.lastActivity ?? chat.createdAt),
        )}
      </Text>
    </div>
  );
};

export interface OpusAgentChatHistoryButtonProps {
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
}

// The drawer-header affordance for the chat history: same chrome as the Ask
// Opus ChatHistoryIconButton (popover + paginated list), backed by
// OpusAgentChats (lastActivity ordering is the server default).
export const OpusAgentChatHistoryButton: React.FC<
  OpusAgentChatHistoryButtonProps
> = ({ activeChatId, onSelectChat }) => {
  const [open, setOpen] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);
  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal>
      <Popover.Trigger asChild>
        <div>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-xs" tabIndex={-1}>
                <MsgClock1Icon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">View chat history</TooltipContent>
          </Tooltip>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          className="z-50 max-h-[500px] w-[320px] rounded-lg border border-border-primary bg-white p-0 shadow-lg focus:outline-none"
        >
          <div className="border-b border-gray-2 px-2.5 py-2">
            <Text variant="compact-title">Chat history</Text>
          </div>
          <ScrollableFlexGrow className="h-[320px]" innerRef={scrollableRef}>
            <PaginatedList<
              OpusAgentChatListItemFragment,
              typeof useOpusAgentChatsQuery
            >
              queryHook={useChatsListQuery}
              getTotalItemCount={(chatsData) =>
                chatsData?.OpusAgentChats?.totalCount ?? 0
              }
              getItems={(chatsData) => chatsData?.OpusAgentChats?.objects ?? []}
              itemId={(chat) => chat.id}
              input={{}}
              className="flex flex-col gap-0.5 p-1"
              viewportRef={scrollableRef}
              renderItem={(chat) => (
                <ChatRow
                  chat={chat}
                  isActive={chat.id === activeChatId}
                  onSelect={() => {
                    onSelectChat(chat.id);
                    setOpen(false);
                  }}
                />
              )}
              noDataEmptyState={
                <Text variant="fine-print-description" color="muted">
                  No chat history
                </Text>
              }
              dataTestId="opus-agent-chat-history-list"
            />
          </ScrollableFlexGrow>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default OpusAgentChatHistoryButton;

gql`
  fragment OpusAgentChatListItem on OpusAgentChat {
    model
    id
    title
    titleSource
    lastActivity
    createdAt
  }

  query OpusAgentChats(
    $input: OpusAgentChatsInput!
    $pagination: PaginationInput
  ) {
    OpusAgentChats(input: $input, pagination: $pagination) {
      objects {
        ...OpusAgentChatListItem
      }
      totalCount
    }
  }

  mutation RenameOpusAgentChat($id: UUID!, $title: String!) {
    renameOpusAgentChat(id: $id, title: $title) {
      success
      error {
        code
        message
      }
      chat {
        ...OpusAgentChatListItem
      }
    }
  }
`;
